/*
 * Copyright (c) 2026. Bolmate B.V.
 *
 * Pure helpers behind the per-notification channels select ("stuur een
 * notificatie via ..."): which options are checked, how a device row is
 * labeled, and how a notification's channel list changes when the user
 * toggles e-mail. Kept free of React/network so the decision logic is unit
 * testable; the component (notificationChannelsSelect.tsx) does the wiring.
 */

import {DeviceTokenChannel} from './requestSender';

/** Structural view of a backend device-token row — matches the rows returned
 *  by listDeviceTokens without importing the component-local interfaces. */
export interface PushDeviceLike {
    id: string;
    platform?: string | null;
    custom_name?: string | null;
    user_agent?: string | null;
    endpoint?: string | null;
}

/** Option value for the e-mail channel. Matches the backend channel name so
 *  the selected-values array reads naturally in tests/devtools. */
export const EMAIL_CHANNEL_VALUE = 'EMAIL';

/** Synthetic option value for the current browser/device while it has no
 *  backend token yet (checking it starts the subscribe flow). */
export const THIS_DEVICE_VALUE = 'this-device';

const DEVICE_VALUE_PREFIX = 'device:';

export function deviceOptionValue(deviceId: string): string {
    return DEVICE_VALUE_PREFIX + deviceId;
}

export function isDeviceOptionValue(value: string): boolean {
    return value.startsWith(DEVICE_VALUE_PREFIX);
}

export function deviceIdFromOptionValue(value: string): string {
    return value.slice(DEVICE_VALUE_PREFIX.length);
}

/** True when the token row belongs to THIS browser (endpoint match). */
export function isCurrentDevice(device: PushDeviceLike, currentEndpoint: string | null): boolean {
    return !!(currentEndpoint && device.endpoint && device.endpoint === currentEndpoint);
}

// Drop the "<device type> – " prefix from the stored label — the option's
// device-type icon already conveys it. Format is "Computer – macOS 26,
// Chrome"; keep everything after the FIRST " – " (or " - ") separator.
// Locale-independent; a prefix-less label is returned unchanged.
export function stripDeviceTypePrefix(label: string): string {
    const match = label.match(/^.+? [–-] (.+)$/);
    return match ? match[1] : label;
}

export type DeviceFormFactor = 'desktop' | 'mobile' | 'tablet';

/** Wording for a device row. `thisDevice` / `formFactor` are keyed by form
 *  factor so the row can name the thing in front of the user ("This phone")
 *  instead of a model number nobody recognises. */
export interface DeviceNameLabels {
    installedApp: string;
    unknownDevice: string;
    thisDevice: Record<DeviceFormFactor, string>;
    formFactor: Record<DeviceFormFactor, string>;
}

/** Display name for a device row.
 *
 *  A raw label like "ASUS_AI2302, Bolmate app" means nothing to the reader —
 *  a model number rings no bell, and "Bolmate app" does not say which device it
 *  is on. So the device the user is holding says **"This phone"**, and any other
 *  device leads with its form factor: **"Phone, ASUS_AI2302, Bolmate app"**.
 *  A nickname the user set themselves always wins over both. */
export function deviceDisplayName(
    device: PushDeviceLike,
    labels: DeviceNameLabels,
    isCurrent: boolean = false
): string {
    const custom = (device.custom_name || '').trim();
    if (custom) return custom;

    const formFactor = deviceTypeIcon(device);
    if (isCurrent) return labels.thisDevice[formFactor];

    const stripped = stripDeviceTypePrefix(device.platform || device.user_agent || '');
    const label = stripped.replace('(PWA)', `(${labels.installedApp})`);
    if (!label) return labels.unknownDevice;
    return `${labels.formFactor[formFactor]}, ${label}`;
}

/** Form factor of the browser this code is running in — for the row that offers
 *  to subscribe THIS device, which has no stored token to read it from. */
export function currentDeviceFormFactor(): DeviceFormFactor {
    if (typeof navigator === 'undefined') return 'desktop';
    return deviceTypeIcon({id: 'current', user_agent: navigator.userAgent});
}

// Pick a device-type icon from the STORED user_agent (language-independent —
// device.platform is localized, so we don't key on it). Falls back to the
// label only when no user_agent is stored.
export function deviceTypeIcon(device: PushDeviceLike): DeviceFormFactor {
    const ua = device.user_agent || '';
    if (/iPad/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) return 'tablet';
    if (/iPhone|iPod|Mobile|webOS|BlackBerry|IEMobile/.test(ua)) return 'mobile';
    if (!ua) {
        const label = device.platform || '';
        if (/tablet/i.test(label)) return 'tablet';
        if (/mobile|phone|telefoon|mobiel/i.test(label)) return 'mobile';
    }
    return 'desktop';
}

/** Channels of a notification, with the legacy fallback for rows fetched
 *  before the `channels` field landed (stored inside flex). Defaults to
 *  e-mail only — every notification always had e-mail delivery. */
export function readNotificationChannels(notification: {
    channels?: Array<string> | null;
    flex?: unknown;
}): Array<DeviceTokenChannel> {
    // An explicit array is authoritative — even when EMPTY. `[]` is the
    // deliberate "delivers nowhere" state (a push-only notification whose last
    // targeted device was deleted); the backend's get_channels keys on
    // `is not None`, NOT truthiness, precisely so it is never resurrected to
    // the e-mail default. Only a null/undefined field (a legacy row predating
    // the channels field) falls through to the flex / e-mail fallback.
    if (Array.isArray(notification.channels)) {
        return notification.channels as Array<DeviceTokenChannel>;
    }
    const flex = notification.flex as {channels?: Array<string>} | null | undefined;
    if (flex && Array.isArray(flex.channels)) {
        return flex.channels as Array<DeviceTokenChannel>;
    }
    return ['EMAIL'];
}

/** The notification's push-device targeting list (flex.push_device_ids):
 *  which device tokens this notification pushes to. `null` = key absent =
 *  "all devices" (legacy rows created before per-device targeting). The
 *  backend never stores an empty list — clearing the last device strips
 *  WEB_PUSH and removes the key — so [] never round-trips. Members are
 *  coerced to string defensively (ids serialize as UUID strings). */
export function readNotificationPushDeviceIds(notification: {flex?: unknown}): Array<string> | null {
    const flex = notification.flex as {push_device_ids?: unknown} | null | undefined;
    if (!flex || !Array.isArray(flex.push_device_ids)) return null;
    return flex.push_device_ids.map(id => String(id));
}

/** How many of the account's subscribed devices this targeting list actually
 *  reaches. `null` (= all devices) → every subscribed device; an explicit
 *  list → its intersection with the subscribed set (ids of deleted devices
 *  linger in the list until the next PATCH — they deliver nowhere). */
export function countEffectivePushTargets(deviceIds: Array<string> | null, devices: Array<PushDeviceLike>): number {
    if (deviceIds === null) return devices.length;
    return devices.filter(device => deviceIds.includes(device.id)).length;
}

/** The checked values of the channels select — derived PURELY from backend
 *  state so every browser shows the same thing (single source of truth).
 *  E-mail mirrors THIS notification's channel list. A device row is checked
 *  when the notification carries WEB_PUSH AND its targeting list covers the
 *  device (`null` = all devices) — so a checked device always means "this
 *  notification reaches this device". No per-browser localStorage (opt-in)
 *  is consulted here: that only gates the subscribe FLOW, not which devices
 *  a notification targets. */
export function computeSelectedChannelValues(args: {
    channels: Array<DeviceTokenChannel>;
    devices: Array<PushDeviceLike>;
    deviceIds: Array<string> | null;
}): Array<string> {
    const {channels, devices, deviceIds} = args;
    const values: Array<string> = [];
    if (channels.includes('EMAIL')) values.push(EMAIL_CHANNEL_VALUE);
    if (!channels.includes('WEB_PUSH')) return values;
    for (const device of devices) {
        if (deviceIds === null || deviceIds.includes(device.id)) values.push(deviceOptionValue(device.id));
    }
    return values;
}

/** THE one guard every mutation of the channels select must pass: true when
 *  the resulting configuration would deliver NOWHERE — no e-mail channel and
 *  no effectively-targeted push device. The select must never reach zero
 *  selected values; callers block the change with the min-one-channel toast
 *  instead of firing the PATCH. */
export function wouldLeaveNoDelivery(
    nextChannels: Array<DeviceTokenChannel>,
    nextDeviceIds: Array<string> | null,
    subscribedDevices: Array<PushDeviceLike>
): boolean {
    if (nextChannels.includes('EMAIL')) return false;
    if (!nextChannels.includes('WEB_PUSH')) return true;
    return countEffectivePushTargets(nextDeviceIds, subscribedDevices) === 0;
}

/** The add-notification wizard's device selection mapped to the stored
 *  `push_device_ids` targeting: ALWAYS the explicit id list of the selection
 *  (intersected with the subscribed set — stale-row hygiene — and deduped).
 *  Never `null`: null targeting means "all devices, INCLUDING devices
 *  subscribed in the future", so a notification created that way silently
 *  adopts every later-subscribed device — the first push enable would light
 *  up every such notification at once (the all-configs-flip bug). The
 *  synthetic "this device" option is the caller's concern: its
 *  freshly-registered token id is appended after the subscribe flow
 *  resolves it. */
export function computeWizardPushTargets(args: {
    selectedIds: Array<string>;
    subscribedIds: Array<string>;
}): Array<string> {
    const {selectedIds, subscribedIds} = args;
    const seen = new Set<string>();
    return selectedIds.filter(id => {
        if (!subscribedIds.includes(id) || seen.has(id)) return false;
        seen.add(id);
        return true;
    });
}

/** Next channel list after toggling e-mail on/off. Preserves the other
 *  channels and never duplicates. */
export function toggleEmailChannel(channels: Array<DeviceTokenChannel>, on: boolean): Array<DeviceTokenChannel> {
    const without = channels.filter(channel => channel !== 'EMAIL');
    return on ? ['EMAIL', ...without] : without;
}

/** Next channel list guaranteeing WEB_PUSH is present (used right after the
 *  current device subscribes — checking a device means "push this
 *  notification here", so the notification must carry the push channel). */
export function ensurePushChannel(channels: Array<DeviceTokenChannel>): Array<DeviceTokenChannel> {
    return channels.includes('WEB_PUSH') ? channels : [...channels, 'WEB_PUSH'];
}

/** Next channel list after toggling push on/off for ONE notification. Push
 *  and e-mail are toggled independently per notification — turning push off
 *  here never touches the device's subscription (which is account-global). */
export function togglePushChannel(channels: Array<DeviceTokenChannel>, on: boolean): Array<DeviceTokenChannel> {
    if (on) return ensurePushChannel(channels);
    return channels.filter(channel => channel !== 'WEB_PUSH');
}
